import {announce} from '@github-ui/aria-live'
import {SoftNavOnlyLink as Link} from '@github-ui/react-router'
import {useSearchParams} from '@github-ui/use-navigate'
import {BookmarkIcon, ChevronDownIcon, ChevronRightIcon} from '@primer/octicons-react'
import {CounterLabel, IconButton} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {useCallback, useId, useMemo, useRef, useState} from 'react'

import {LABELS} from '../../constants'
import type {SavedView, SavedViewVisibility} from '../../types'
import type {PinControls} from './PinViewMenuItem'
import styles from './RepositoryViews.module.css'
import {applySectionSort, resolveSectionSort} from './views-section-sort'
import {sortViews, type ViewsSortValue} from './views-sort-options'
import {ViewsList} from './ViewsList'
import {ViewsSortMenu} from './ViewsSortMenu'

const SECTION_PREVIEW_COUNT = 10

const SECTION_LABELS: Record<
  SavedViewVisibility,
  {title: string; emptyHeading: string; emptyCta: string; emptyCtaSuffix: string}
> = {
  personal: {
    title: LABELS.views.personal,
    emptyHeading: LABELS.views.emptyPersonal,
    emptyCta: LABELS.views.emptyPersonalCta,
    emptyCtaSuffix: LABELS.views.emptyPersonalCtaSuffix,
  },
  team: {
    title: LABELS.views.team,
    emptyHeading: LABELS.views.emptyTeam,
    emptyCta: LABELS.views.emptyTeamCta,
    emptyCtaSuffix: LABELS.views.emptyTeamCtaSuffix,
  },
}

type ViewsSectionProps = {
  visibility: SavedViewVisibility
  views: SavedView[]
  otherSectionsHaveViews: boolean
  owner: string
  repo: string
  newViewUrl: string
  viewerCanManageViews?: boolean
  showPinControls?: boolean
  pinControls?: PinControls
}

export function ViewsSection({
  visibility,
  views,
  otherSectionsHaveViews,
  owner,
  repo,
  newViewUrl,
  viewerCanManageViews = false,
  showPinControls = false,
  pinControls,
}: ViewsSectionProps) {
  const title = SECTION_LABELS[visibility].title
  const headingId = useId()
  const bodyRef = useRef<HTMLDivElement>(null)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const sort = resolveSectionSort(searchParams, visibility)
  const [userCollapsed, setUserCollapsed] = useState<boolean | undefined>(undefined)
  const [expanded, setExpanded] = useState(false)

  const handleSortChange = useCallback(
    (next: ViewsSortValue) => {
      setSearchParams(prev => applySectionSort(prev, visibility, next), {replace: true, preventAutofocus: true})
    },
    [setSearchParams, visibility],
  )

  const sortedViews = useMemo(
    () => sortViews(views, sort, {pinnedFirst: showPinControls}),
    [views, sort, showPinControls],
  )
  const hasViews = views.length > 0
  const collapsed = userCollapsed ?? (!hasViews && otherSectionsHaveViews)
  const visibleViews = expanded ? sortedViews : sortedViews.slice(0, SECTION_PREVIEW_COUNT)
  const hasMore = sortedViews.length > visibleViews.length
  const section = useMemo(
    () => ({
      title,
      isLastView: (deletedId: SavedView['id']) => sortedViews.length === 1 && sortedViews[0]?.id === deletedId,
      otherSectionsHaveViews,
      headingRef,
    }),
    [title, sortedViews, otherSectionsHaveViews],
  )

  const revealAll = () => {
    setExpanded(true)
    announce(`Showing all ${sortedViews.length} ${title} views`)
    requestAnimationFrame(() => {
      const rows = bodyRef.current?.querySelectorAll<HTMLElement>('[data-saved-view-id]')
      rows?.[SECTION_PREVIEW_COUNT]?.focus()
    })
  }

  return (
    <section className={styles.section} aria-labelledby={headingId}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionTitleGroup}>
          <IconButton
            className={styles.collapseButton}
            icon={collapsed ? ChevronRightIcon : ChevronDownIcon}
            aria-label={collapsed ? `Expand ${title}` : `Collapse ${title}`}
            aria-expanded={!collapsed}
            variant="invisible"
            size="small"
            onClick={() => setUserCollapsed(!collapsed)}
          />
          <h2 id={headingId} ref={headingRef} className={styles.sectionHeading}>
            {title}
          </h2>
          <CounterLabel>{views.length}</CounterLabel>
        </div>
        {hasViews && !collapsed && <ViewsSortMenu sort={sort} onSortChange={handleSortChange} />}
      </div>
      {!collapsed && (
        <div ref={bodyRef} className={styles.viewsListWrapper}>
          <ViewsList
            views={visibleViews}
            owner={owner}
            repo={repo}
            sort={sort}
            onSortChange={handleSortChange}
            viewerCanManageViews={viewerCanManageViews}
            showPinControls={showPinControls}
            pinControls={pinControls}
            title={title}
            ariaLabelledBy={headingId}
            section={section}
            hideMetadata
            emptyState={
              <SectionEmpty
                visibility={visibility}
                newViewUrl={newViewUrl}
                viewerCanManageViews={viewerCanManageViews}
              />
            }
          />
          {hasMore && (
            <button
              type="button"
              className={styles.showMore}
              aria-label={`Show more ${title} views`}
              onClick={revealAll}
            >
              {LABELS.views.showMore}
            </button>
          )}
        </div>
      )}
    </section>
  )
}

function SectionEmpty({
  visibility,
  newViewUrl,
  viewerCanManageViews,
}: {
  visibility: SavedViewVisibility
  newViewUrl: string
  viewerCanManageViews: boolean
}) {
  const {emptyHeading, emptyCta, emptyCtaSuffix} = SECTION_LABELS[visibility]
  return (
    <Blankslate>
      <Blankslate.Visual>
        <BookmarkIcon size="medium" />
      </Blankslate.Visual>
      <Blankslate.Heading as="h3">{emptyHeading}</Blankslate.Heading>
      {viewerCanManageViews && (
        <Blankslate.Description>
          <Link className="Link--inTextBlock" to={`${newViewUrl}?visibility=${visibility}`}>
            {emptyCta}
          </Link>{' '}
          {emptyCtaSuffix}
        </Blankslate.Description>
      )}
    </Blankslate>
  )
}
